import {
  differenceInCalendarDays,
  format,
  formatDistanceToNow,
  isFuture,
  parseISO,
} from 'date-fns';

export function getRenewalStatus(seller, short = false) {
  const trialEndsAt = seller.trialEnd || null;
  const renewsAt = seller.stripeSubscriptionCurrentPeriodEnd || null;
  const cancelsAt = seller.stripeSubscriptionCancelAt || null;
  const isActive = seller.subscriptionStatus === 'active';
  const isTrialing = seller.subscriptionStatus === 'trialing';
  const isExpired = seller.subscriptionStatus === 'trial_expired';
  const isPastDue = seller.stripeSubscriptionStatus === 'past_due';
  const hasPendingPlanChange = seller.pendingPlanChange?.stripeSubscriptionScheduleId;

  // pending plan changes are always downgrades
  if (hasPendingPlanChange) {
    const pendingPlan = seller.pendingPlanChange.plan;
    if (pendingPlan.handle === 'scale' && pendingPlan.bracket) {
      return `Downgrades to ${pendingPlan.title} ($${pendingPlan.bracket / 1000}K) on ${format(
        new Date(seller.pendingPlanChange.effectiveDate),
        'd MMM, yyyy',
      )}`;
    }

    return `Downgrades to ${seller.pendingPlanChange.plan.title} on ${format(
      new Date(seller.pendingPlanChange.effectiveDate),
      'd MMM, yyyy',
    )}`;
  }

  // canceling in the future
  if (cancelsAt) {
    if (short) {
      return `cancels in ${formatDistanceToNow(new Date(cancelsAt))}`;
    }

    return `cancels on ${format(new Date(cancelsAt), 'd MMM, yyyy')}`;
  }

  // trialing
  if (isFuture(new Date(trialEndsAt)) && isTrialing) {
    if (short) {
      return `trialing, ends in ${formatDistanceToNow(new Date(trialEndsAt))}`;
    }
    return `trialing, ends on ${format(new Date(trialEndsAt), 'd MMM, yyyy')}`;
  }

  // subscribed but still in trial
  if (isFuture(new Date(trialEndsAt)) && isActive) {
    if (short) {
      return `trialing, starts in ${formatDistanceToNow(new Date(trialEndsAt))}`;
    }

    return `trialing, starts on ${format(new Date(trialEndsAt), 'd MMM, yyyy')}`;
  }

  // expired trial
  if (isExpired) {
    if (short) {
      return `trial expired ${formatDistanceToNow(new Date(trialEndsAt))} ago`;
    }

    return `trial expired on ${format(new Date(trialEndsAt), 'd MMM, yyyy')}`;
  }

  // free plan
  if (seller.subscriptionStatus === 'free') {
    return 'free plan';
  }

  // admin-granted paid plan, no Stripe subscription to renew
  if (seller.subscriptionStatus === 'comped') {
    return 'comped';
  }

  // no subscription
  if (!renewsAt) {
    return 'no subscription';
  }

  if (isPastDue) {
    return `past due since ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
  }

  if (short) {
    return `renews in ${formatDistanceToNow(new Date(renewsAt))}`;
  }

  return `renews on ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
}

const CONTRACT_ENDING_SOON_DAYS = 60;

// Contract dates are stored as UTC midnight; read the calendar day off the string
// so a viewer west of UTC doesn't see the previous day.
const parseContractDate = isoDate => parseISO(isoDate.slice(0, 10));

export function formatContractDate(isoDate) {
  return isoDate ? format(parseContractDate(isoDate), 'd MMM, yyyy') : 'Not set';
}

export function toContractDateInput(isoDate) {
  return isoDate ? isoDate.slice(0, 10) : '';
}

export function fromContractDateInput(value) {
  return value ? `${value}T00:00:00.000Z` : null;
}

export function getContractEndStatus(contractEndDate, now = new Date()) {
  if (!contractEndDate) return null;

  const daysLeft = differenceInCalendarDays(parseContractDate(contractEndDate), now);

  if (daysLeft < 0) {
    const daysAgo = -daysLeft;
    return {
      label: `Ended ${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`,
      variant: 'destructive',
    };
  }

  if (daysLeft === 0) return { label: 'Ends today', variant: 'warning' };

  if (daysLeft <= CONTRACT_ENDING_SOON_DAYS) {
    return { label: `Ends in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`, variant: 'warning' };
  }

  return null;
}
